import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { getAnalyticsAdapter, setAnalyticsAdapter } from "../analytics/AnalyticsAdapter";
import { buildAnalytics } from "../analytics/buildAnalytics";
import { getOrCreateAnonId } from "../analytics/anonId";
import { resolveInternalFlag } from "../analytics/internal";
import { HEARTBEAT_MS, createSessionTracker, setSessionTracker } from "../analytics/session";
import { useGameStore } from "../state/store";
import { readStoredLanguage, resolveStartupLanguage } from "../state/languagePreference";
import { initI18n } from "../i18n";
import {
  LocalPlatformAdapter,
  getPlatformAdapter,
  setPlatformAdapter,
  type AdEvent,
} from "../platform/PlatformAdapter";
import { selectPlatform } from "../platform/selectPlatform";
import { isExternalAnalyticsAllowed, useConsentStore } from "../state/consentStore";
import { App } from "./App";
import { setAdPaused } from "./adPause";

import "../styles/tokens.css";

async function initPlatform(): Promise<void> {
  const target = import.meta.env.VITE_PLATFORM;
  const platform = selectPlatform(target);
  platform.onAdEvent((event: AdEvent) => {
    setAdPaused(event.type === "adStarted");
  });
  try {
    await platform.init();
    setPlatformAdapter(platform);
    platform.notifyLoaded();
    platform.gameReady();
  } catch (error) {
    setPlatformAdapter(new LocalPlatformAdapter());
    getAnalyticsAdapter().track("sdk_error", {
      sdk: target || "unknown",
      method: "init",
      message: String(error),
    });
  }
}

function initLanguage(): void {
  const stored = readStoredLanguage();
  if (stored) {
    return;
  }
  const platform = getPlatformAdapter().getDetectedLanguage?.() ?? null;
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  useGameStore.getState().applyDetectedLanguage(resolveStartupLanguage({ stored, platform, preferred }));
}

function renderApp(): void {
  const container = document.getElementById("root");
  if (!container) {
    throw new Error("Root element #root not found");
  }
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

function initAnalytics(): void {
  const externalEnabled = isExternalAnalyticsAllowed(useConsentStore.getState().consent);
  setAnalyticsAdapter(buildAnalytics(import.meta.env, { externalEnabled }));
  const analytics = getAnalyticsAdapter();
  analytics.identify(getOrCreateAnonId());
  if (resolveInternalFlag(window.location.search)) {
    analytics.setUserProps({ internal: true });
  }
  analytics.track("app_open");
  initSession();
}

function initSession(): void {
  const tracker = createSessionTracker({
    track: (event, props) => getAnalyticsAdapter().track(event, props),
    now: Date.now,
    sessionId: crypto.randomUUID(),
  });
  setSessionTracker(tracker);
  tracker.start();
  tracker.noteScreen(useGameStore.getState().currentScreen);
  useGameStore.subscribe((state, prev) => {
    if (state.currentScreen !== prev.currentScreen) {
      tracker.noteScreen(state.currentScreen);
    }
  });
  window.setInterval(() => tracker.heartbeat(document.visibilityState === "visible"), HEARTBEAT_MS);
  window.addEventListener("pagehide", () => {
    tracker.end();
    getAnalyticsAdapter().flush();
  });
}

/**
 * Composition root: assembles analytics, selects and initialises the platform
 * adapter (with a hard fallback to Local), activates i18n, then mounts the app.
 */
export async function bootstrap(): Promise<void> {
  initAnalytics();
  await initPlatform();
  initLanguage();
  await initI18n();
  renderApp();
}
